/**
 * Per-route React error boundary. Wrapped around `<Outlet />` in the
 * app shell so an uncaught render error on one route does not blank
 * the whole signed-in app. The fallback renders inside the shell, so
 * the sidebar and topbar stay usable.
 *
 * Function components cannot catch errors in React 18 — this is the
 * only class component in the UI primitives folder. Reset path:
 * "Try Again" resets the internal error state (re-renders children);
 * "Reload Page" falls back to a full reload if a render keeps throwing.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

import styles from './error-boundary.module.css';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, info);
    }
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div className={styles.wrap} role="alert">
        <div className={styles.card}>
          <h1 className={styles.title}>Something Went Wrong.</h1>
          <p className={styles.sub}>The page hit an unexpected error. Try again, or reload.</p>
          <pre className={styles.detail}>
            <strong>{error.name}:</strong> {error.message}
          </pre>
          <div className={styles.actions}>
            <button type="button" className={styles.primary} onClick={this.reset}>
              Try Again
            </button>
            <button type="button" className={styles.secondary} onClick={this.reload}>
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
